import { forwardRef, useEffect, useState } from "react";
import {
    Button,
    ForwardRefComponent,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
} from "@fluentui/react-components";
import {
    ArrowDownloadRegular,
    BracesRegular,
    DocumentTableRegular,
    DocumentTextRegular,
} from "@fluentui/react-icons";
import { IExportReportButtonProps } from "./export-report-button.props";
import { ILocaleStrings } from "loc/locale.interfaces";
import { useL10n } from "context/l10n-context";
import { downloadAsJson } from "./formatters/json-formatter";
import { downloadAsCsv } from "./formatters/csv-formatter";
import { downloadAsXlsx } from "./formatters/xlsx-formatter";

type ExportFormat = "xlsx" | "csv" | "json";

export const ExportReportButton: ForwardRefComponent<IExportReportButtonProps> = forwardRef((props, ref) => {
    const { t } = useL10n<ILocaleStrings>();

    const [disabled, setDisabled] = useState<boolean>(true);

    useEffect(() => {
        setDisabled((props.items && props.items.length > 0) !== true);
    }, [props.items]);

    const onExport = (format: ExportFormat) => {
        const prefix = t(props.defaultFilenamePrefix);
        const columns = props.columns;
        const items = props.items ?? [];

        if (format === "json") {
            downloadAsJson(columns, items, prefix);
            return;
        }
        if (format === "csv") {
            downloadAsCsv(columns, items, prefix);
            return;
        }
        const { truncatedCells } = downloadAsXlsx(columns, items, prefix);
        if (truncatedCells > 0) {
            props.onExportNotice?.({ kind: "xlsxTruncation", truncatedCells });
        }
    };

    const menuItems = (
        <MenuList>
            <MenuItem icon={<DocumentTableRegular />} onClick={() => onExport("xlsx")}>{t("ppaExportAsExcel")}</MenuItem>
            <MenuItem icon={<DocumentTextRegular />} onClick={() => onExport("csv")}>{t("ppaExportAsCsv")}</MenuItem>
            <MenuItem icon={<BracesRegular />} onClick={() => onExport("json")}>{t("ppaExportAsJson")}</MenuItem>
        </MenuList>
    );

    if (props.isOverflowingMenu) {
        return (
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <MenuItem icon={<ArrowDownloadRegular />} disabled={disabled}>
                        {t("export")}
                    </MenuItem>
                </MenuTrigger>
                <MenuPopover>{menuItems}</MenuPopover>
            </Menu>
        );
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Button
                    ref={ref}
                    icon={<ArrowDownloadRegular />}
                    disabled={disabled}
                    appearance="subtle"
                >
                    {t("export")}
                </Button>
            </MenuTrigger>
            <MenuPopover>{menuItems}</MenuPopover>
        </Menu>
    );
});
